'use client';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CheckCircle,
  Copy,
  Link2 as LinkIcon,
  Mail as MailIcon,
  QrCode,
  Share2,
  X,
} from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';
import {
  FacebookIcon,
  TelegramIcon,
  TwitterIcon,
  WhatsappIcon,
} from 'react-share';

interface BetSlipPopoutProps {
  isOpen: boolean;
  onClose: () => void;
}

const MessengerIcon = () => (
  <svg
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>Messenger</title>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18 36c9.941 0 18-7.753 18-17.313S27.941 1.375 18 1.375 0 9.128 0 18.687c0 4.417 1.763 8.448 4.718 11.53v4.205c0 .736.815 1.18 1.439.787l4.062-2.554a18.895 18.895 0 0 0 7.781 1.658z"
      fill="url(#messenger_paint0_linear)"
    />
    <path
      d="M7.78 22.148l5.244-8.318c.613-.974 1.954-1.168 2.808-.408l3.96 3.53a.85.85 0 0 0 1.057.065l5.525-4.108c.55-.409 1.258.261.884.854l-5.244 8.318c-.613.974-1.954 1.168-2.808.408l-3.96-3.53a.85.85 0 0 0-1.057-.065l-5.525 4.108c-.55.409-1.258-.261-.884-.854z"
      fill="#fff"
    />
    <defs>
      <linearGradient
        id="messenger_paint0_linear"
        x1="5.772"
        y1="36.568"
        x2="33.159"
        y2="4.088"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#00B2FF" />
        <stop offset="1" stopColor="#006AFF" />
      </linearGradient>
    </defs>
  </svg>
);

const TikTokIcon = () => (
  <svg
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>TikTok</title>
    <circle cx="18" cy="18" r="18" fill="black" />
    <path
      d="M21.15 15.187c0 0-.337-2.812-2.925-3.037v10.912c0 1.463-1.237 2.7-2.7 2.7s-2.7-1.238-2.7-2.7c0-1.463 1.237-2.7 2.7-2.7.45 0 .787.112 1.125.337v-3.037c-.337-.113-.787-.113-1.125-.113-3.038 0-5.513 2.475-5.513 5.513s2.475 5.512 5.513 5.512c3.037 0 5.512-2.475 5.512-5.512V17.775c1.688.9 2.7 2.475 2.813 2.7v-3.038c-.788-.45-1.8-1.237-2.7-2.25z"
      fill="#fff"
    />
    <path
      d="M21.15 15.187c0 0-.337-2.812-2.925-3.037v.45c1.913.338 2.588 2.588 2.588 2.588l.337-.001z"
      fill="#FF0050"
    />
    <path
      d="M23.85 20.475c-.113-.225-1.125-1.8-2.813-2.7v.563c1.35.787 2.138 2.362 2.363 2.475l.45-.338z"
      fill="#00F2FE"
    />
  </svg>
);

export const BetSlipPopout = ({ isOpen, onClose }: BetSlipPopoutProps) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setToastMessage('Copied to clipboard!');
    setTimeout(() => setToastMessage(null), 3000);
  };

  const handleSaveQr = () => {
    const link = document.createElement('a');
    link.href = '/Final_NovaMobile_Assets/05_Refer&EarnNew/qrcode.png';
    link.download = 'qrcode.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setToastMessage('Image saved to your pc!');
    setTimeout(() => setToastMessage(null), 3000);
    setIsQrModalOpen(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-100 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full max-w-[340px] rounded-2xl shadow-2xl"
            style={{
              padding: '1px',
              background:
                'linear-gradient(135deg, #D97AFF 0%, #B95CD7 29%, #9035A6 52%, #D9801B 93%)',
              boxShadow: '0 0 24px rgba(217, 122, 255, 0.3)',
            }}
          >
            {/* Card inner — background image lives here */}
            <div
              className="relative w-full rounded-2xl overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #180530 0%, #0a0118 100%)',
              }}
            >
              {/* Background image */}
              <div className="absolute inset-0 z-0">
                <Image
                  src="/Final_NovaMobile_Assets/09_Games/AllPlay_Popout/Cosmic.png"
                  alt="background"
                  fill
                  className="object-cover opacity-30"
                />
              </div>

              {/* Starry background effect overlay */}
              <div
                className="absolute inset-0 z-[1] pointer-events-none"
                style={{
                  backgroundImage:
                    'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 1px, transparent 1px)',
                  backgroundSize: '24px 24px',
                  opacity: 0.3,
                }}
              />

              {/* Glowing top accent */}
              <div className="absolute top-0 left-0 right-0 z-[2] h-[1px] bg-gradient-to-r from-transparent via-[#D281FF] to-transparent opacity-70" />

              {/* Header */}
              <div className="relative z-50 flex items-center justify-center pt-5 pb-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="absolute left-4 top-5 rounded-full bg-white/10 p-1 hover:bg-white/20 transition-colors"
                >
                  <X className="size-4 text-white/70" />
                </button>
                <h2
                  className="text-lg font-bold tracking-wider text-[#FFE27A]"
                  style={{ textShadow: '0 0 10px rgba(255,226,122,0.4)' }}
                >
                  BET SLIP
                </h2>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 bg-[#FE1BE7] w-[40%] h-[40%] rounded-[50%] opacity-50 blur-[40px]" />
              </div>

              {/* Header separator with glow */}
              <div
                className=" relative z-30 h-[1px] w-full bg-gradient-to-r from-transparent via-[#D97AFF] via-[#FFFFFF] via-[#D97AFF] to-transparent shadow-[0_0_8px_#D97AFF]"
                style={{
                  background:
                    'linear-gradient(90deg, rgba(144,53,166,0) 0%, #D97AFF 29%, #FFFFFF 47%, #D97AFF 70%, rgba(144,53,166,0) 100%)',
                }}
              >
                <div className="absolute top-0 left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 w-[121px] h-[6px] opacity-50 bg-[#FE1BE7] rounded-[50%] blur-[4px]"></div>
              </div>

              {/* Game Info */}
              <div className="relative z-10 flex px-5 pt-6 pb-4 gap-4">
                <div
                  className="size-[72px] shrink-0 rounded-xl overflow-hidden"
                  style={{
                    padding: '0.75px',
                    background:
                      'linear-gradient(135deg, #D97AFF 0%, #B95CD7 29%, #9035A6 52%, #D9801B 93%)',
                    boxShadow: '0 0 12px rgba(217, 122, 255, 0.35)',
                  }}
                >
                  <div
                    className="w-full h-full rounded-[10px] overflow-hidden"
                    style={{
                      background:
                        'linear-gradient(135deg, #2A0A4A 0%, #1A0530 60%, #3A1A10 100%)',
                    }}
                  >
                    <Image
                      src="/Final_NovaMobile_Assets/09_Games/AllPlay_Popout/FC_28001.avif"
                      alt="Lucky Spin"
                      width={72}
                      height={72}
                      className="object-cover w-full h-full"
                    />
                  </div>
                </div>
                <div className="flex flex-col justify-center">
                  <h3 className="text-white font-semibold text-lg leading-tight mb-1">
                    Lucky Spin
                  </h3>
                  <p className="text-[#CEA8F1] text-[11px] mb-1">
                    3 Oak Gaming
                  </p>
                  <div className="flex items-center gap-2 bg-[#6F2A8E] rounded-[5px] px-1 py-0.5 text-sm uppercase tracking-wider text-[#CEA8F1] font-normal">
                    <span>Slot</span>
                    <span>RTP: 97.88%</span>
                  </div>
                </div>
              </div>

              {/* Payout Box */}
              <div className="relative z-10 mx-5 my-2 ">
                {/* Outer SVG gradient border */}
                <svg
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  xmlns="http://www.w3.org/2000/svg"
                  preserveAspectRatio="none"
                  role="img"
                  aria-label="Border gradient"
                >
                  <title>Border gradient</title>
                  <defs>
                    <linearGradient
                      id="payoutBorder"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor="#D97AFF" />
                      <stop offset="29%" stopColor="#B95CD7" />
                      <stop offset="94%" stopColor="#9035A6" />
                    </linearGradient>
                  </defs>
                  <rect
                    x="0.5"
                    y="0.5"
                    width="99%"
                    height="99%"
                    rx="11.5"
                    ry="11.5"
                    fill="none"
                    stroke="url(#payoutBorder)"
                    strokeWidth="1"
                  />
                </svg>

                {/* Content */}
                <div className="relative z-10 rounded-xl p-5 flex flex-col items-center">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-[2px]  rounded-[50%] border border-[#DE8933] bg-[#FAE9BC] shadow-[0_0_12px_#FFE27A]blur-[14px] z-2" />
                  <div className="bg-[#D37F34] absolute -top-5 w-[60px] h-[20px] rounded-full blur-[25px] z-1"></div>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/2 h-[2px]  rounded-[50%] border border-[#DE8933] bg-[#FAE9BC] shadow-[0_0_12px_#FFE27A]blur-[14px] z-2" />
                  <div className="bg-[#D37F34] absolute -bottom-5 w-[60px] h-[20px] rounded-full blur-[25px] z-1"></div>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/2 h-[1px] bg-[#7411B2] shadow-[0_0_12px_#7411B2]" />

                  <div className="flex items-center gap-3 w-full mb-3">
                    <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent to-[#FFE27A]/50" />
                    <span className="text-[#FFE27A] text-[11px] font-bold tracking-widest uppercase">
                      Payout
                    </span>
                    <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent to-[#FFE27A]/50" />
                  </div>
                  <div
                    className=" relative z-10 h-[1px] w-full bg-gradient-to-r from-transparent via-[#E3A33A] via-[#FFFFFF] via-[#E3A33A] to-transparent shadow-[0_0_8px_#D97AFF]"
                    style={{
                      background:
                        'linear-gradient(90deg, rgba(144,53,166,0) 0%, #E3A33A 29%, #FFFFFF 47%, #E3A33A 70%, rgba(144,53,166,0) 100%)',
                    }}
                  >
                    <div className="absolute top-0 left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 w-[121px] h-[6px] opacity-50 bg-[#E5A842] rounded-[50%] blur-[4px]"></div>
                  </div>
                  <div className="text-[#70F873] text-[28px] font-bold tracking-tight my-2 drop-shadow-[0_0_8px_rgba(74,222,128,0.3)]">
                    MYR 7,860.00
                  </div>
                  <div
                    className=" relative z-10 h-[0.5px] w-full bg-gradient-to-r from-transparent via-[#E3A33A] via-[#FFFFFF] via-[#E3A33A] to-transparent shadow-[0_0_8px_#D97AFF]"
                    style={{
                      background:
                        'linear-gradient(90deg, rgba(144,53,166,0) 0%, #FFFFFF 29%, #FFFFFF 47%, #FFFFFF 70%, rgba(144,53,166,0) 100%)',
                    }}
                  >
                    {/* <div className="absolute top-0 left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 w-[121px] h-[6px] opacity-50 bg-[#E5A842] rounded-[50%] blur-[4px]"></div> */}
                  </div>
                  <div className="text-white text-[11px] flex items-center justify-center gap-2 mt-2">
                    <span>
                      Bet:{' '}
                      <span className="bg-gradient-to-r from-[#FDEBA9] to-[#E3A33A] bg-clip-text text-transparent">
                        MYR 2,500.00
                      </span>
                    </span>
                    <span className="w-[1px] h-3 bg-[#8E8AA0]/40" />
                    <span>
                      Multiplier:{' '}
                      <span className="bg-gradient-to-r from-[#FDEBA9] to-[#E3A33A] bg-clip-text text-transparent">
                        3.14x
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Details */}
              <div className="relative z-10 px-6 py-4 space-y-2.5">
                <div className="flex justify-between items-center text-[12px]">
                  <span className="text-[#CEA8F1] tracking-wide">
                    Transaction ID
                  </span>
                  <div className="flex items-center gap-1.5 text-[#CEA8F1]">
                    TXN8473926121315
                    <button
                      type="button"
                      className="hover:text-white transition-colors"
                    >
                      <Copy className="size-[14px]" />
                    </button>
                  </div>
                </div>
                <div className="flex justify-between items-center text-[12px]">
                  <span className="text-[#CEA8F1] tracking-wide">Date</span>
                  <span className="text-[#CEA8F1] tracking-wide">
                    03 / 10 / 26 12:08
                  </span>
                </div>
              </div>

              {/* User Section */}
              <div className="relative z-10 py-3 mt-1 border-y border-white/5 bg-gradient-to-r from-transparent via-white/[0.03] to-transparent">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-[1px] bg-gradient-to-r from-[#9035A600] via-[#9035A6] via-[#D97AFF] via-[#9035A6] to-[#9035A600]" />
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-[1px] bg-gradient-to-r from-[#9035A600] via-[#9035A6] via-[#D97AFF] via-[#9035A6] to-[#9035A600]" />
                <div className="flex items-center justify-center gap-1">
                  <div className="size-[20px] rounded-full  flex items-center justify-center text-[#8E8AA0]">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="size-[20px]"
                      role="img"
                      aria-label="User icon"
                    >
                      <title>User icon</title>
                      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                  <span className="text-[#CEA8F1] text-xs">NovaBet88888</span>
                </div>
              </div>

              {/* Buttons */}
              <div className="relative z-10 p-5 space-y-3">
                <button
                  type="button"
                  className="w-full py-3.5 rounded-lg font-bold text-white text-[13px] tracking-wide transition-all active:scale-[0.98] hover:brightness-110"
                  style={{
                    background:
                      'linear-gradient(90deg, #511578 0%, #A44933 50%, #E39414 100%)',
                    boxShadow:
                      '0 4px 15px rgba(227, 148, 20, 0.2), inset 0 1px 0 rgba(255,255,255,0.2)',
                    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                  }}
                >
                  PLAY GAME AGAIN
                </button>
                <button
                  type="button"
                  onClick={() => setIsShareModalOpen(true)}
                  className="relative w-full py-3.5 rounded-lg font-semibold text-[#CEA8F1] text-[13px] tracking-wide border border-[#7411B2]/80 flex items-center justify-center gap-2 transition-all active:scale-[0.98] hover:bg-white/5"
                  style={{
                    background:
                      'linear-gradient(180deg,rgba(78,29,130,0.5)_31%,rgba(17,6,28,0.5)_100%)]',
                  }}
                >
                  <svg
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none"
                    role="img"
                    aria-label="Border gradient"
                  >
                    <title>Border gradient</title>
                    <defs>
                      <linearGradient
                        id="payoutBorder"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                      >
                        <stop offset="0%" stopColor="#D97AFF" />
                        <stop offset="21%" stopColor="#B95CD7" />
                        <stop offset="47%" stopColor="#D97AFF" />
                        <stop offset="60%" stopColor="#9035A6" />
                        <stop offset="93%" stopColor="#D9801B" />
                      </linearGradient>
                    </defs>
                    <rect
                      x="0"
                      y="0"
                      width="100%"
                      height="100%"
                      rx="7"
                      ry="7"
                      fill="none"
                      stroke="url(#payoutBorder)"
                      strokeWidth="1"
                    />
                  </svg>
                  <Share2 className="size-[15px] text[#E3A33A]" />
                  SHARE RESULT
                </button>
              </div>
            </div>
          </motion.div>

          {/* Share to invite — slides up from bottom */}
          <AnimatePresence>
            {isShareModalOpen && (
              // biome-ignore lint/a11y/noStaticElementInteractions: modal backdrop — click closes the sheet
              // biome-ignore lint/a11y/useKeyWithClickEvents: Escape on inner modal handles keyboard dismissal
              <div
                className="fixed inset-0 z-[110] flex items-end justify-center bg-black/60 backdrop-blur-sm"
                onClick={() => setIsShareModalOpen(false)}
              >
                <motion.div
                  initial={{ y: '100%', opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: '100%', opacity: 0 }}
                  transition={{ type: 'spring', damping: 30, stiffness: 280 }}
                  onClick={e => e.stopPropagation()}
                  className="relative w-full max-w-[400px] overflow-hidden rounded-t-[24px] border border-b-0 border-[#6D13A6] p-4 bg-[#180530]"
                >
                  {/* Header */}
                  <div className="flex items-center justify-center relative mb-4">
                    <h3 className="text-base font-semibold text-white">
                      Share to invite
                    </h3>
                    <button
                      type="button"
                      onClick={() => setIsShareModalOpen(false)}
                      className="absolute right-0 flex size-7 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="bg-gradient-to-tr from-[#AF77EC] via-[#6E13AA] via-[60%] to-[#9121D8] p-0.5 rounded-[20px]">
                    <div className="p-4 pb-8 bg-gradient-to-b from-[#1F0834] via-[#551D87] to-[#801AAD] shadow-2xl rounded-[20px]">
                      <p className="mb-4 text-center text-[15px] font-medium text-white/90">
                        Share to
                      </p>

                      <div className="mb-6 flex flex-wrap items-center justify-center gap-3">
                        <button
                          type="button"
                          className="transition-transform hover:-translate-y-1 hover:scale-110"
                        >
                          <FacebookIcon size={36} round />
                        </button>
                        <button
                          type="button"
                          className="transition-transform hover:-translate-y-1 hover:scale-110"
                        >
                          <MessengerIcon />
                        </button>
                        <button
                          type="button"
                          className="transition-transform hover:-translate-y-1 hover:scale-110"
                        >
                          <WhatsappIcon size={36} round />
                        </button>
                        <button
                          type="button"
                          className="transition-transform hover:-translate-y-1 hover:scale-110"
                        >
                          <TelegramIcon size={36} round />
                        </button>
                        <button
                          type="button"
                          className="transition-transform hover:-translate-y-1 hover:scale-110"
                        >
                          <TikTokIcon />
                        </button>
                        <button
                          type="button"
                          className="transition-transform hover:-translate-y-1 hover:scale-110"
                        >
                          <TwitterIcon size={36} round />
                        </button>
                        <button
                          type="button"
                          className="transition-transform hover:-translate-y-1 hover:scale-110 flex items-center justify-center w-[36px] h-[36px] border border-white rounded-full bg-transparent"
                        >
                          <MailIcon size={18} className="text-white" />
                        </button>
                      </div>

                      <div className="space-y-3">
                        <button
                          type="button"
                          onClick={() =>
                            handleCopy('https://beta-app.betnova.world/')
                          }
                          className="w-full flex items-center justify-between rounded-[12px] border border-white/10 bg-[#421D6B] p-3 transition-colors hover:bg-[#4a2178] cursor-pointer group"
                        >
                          <div className="flex items-center gap-3 overflow-hidden">
                            <div className="flex size-8 shrink-0 items-center justify-center text-white/60 group-hover:text-white/80 transition-colors">
                              <LinkIcon size={18} />
                            </div>
                            <div className="flex flex-col overflow-hidden text-left">
                              <span className="text-[11px] text-white/60 font-medium">
                                Referral Link
                              </span>
                              <span className="truncate text-[13px] text-white">
                                https://beta-app.betnova.world/
                              </span>
                            </div>
                          </div>
                          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-white/80 transition-all hover:bg-white/20 hover:text-white group-hover:bg-white/20">
                            <Copy size={14} />
                          </div>
                        </button>

                        <div className="flex gap-2 flex-row flex-col">
                          <button
                            type="button"
                            onClick={() => setIsQrModalOpen(true)}
                            className="flex flex-1 items-center gap-3 rounded-[12px] border border-white/10 bg-[#421D6B] p-3 transition-colors hover:bg-[#4a2178] cursor-pointer group"
                          >
                            <div className="size-[42px] shrink-0 rounded-[6px] bg-white/10 p-2 flex items-center justify-center">
                              <QrCode className="size-full text-white/80" />
                            </div>
                            <span className="text-[10px] text-white/80 pr-2 group-hover:text-white transition-colors">
                              Referral Qr Code
                            </span>
                          </button>

                          <button
                            type="button"
                            onClick={() => handleCopy('NovaBet8888')}
                            className="flex flex-1 items-center justify-between rounded-[12px] border border-white/10 bg-[#421D6B] p-3 transition-colors hover:bg-[#4a2178] cursor-pointer group"
                          >
                            <div className="flex flex-col text-left">
                              <span className="text-[11px] text-white/60 font-medium">
                                Referral Code
                              </span>
                              <span className="text-[13px] text-white">
                                NovaBet8888
                              </span>
                            </div>
                            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-white/80 transition-all hover:bg-white/20 hover:text-white group-hover:bg-white/20">
                              <Copy size={14} />
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>

          {/* Referral QR modal */}
          <AnimatePresence>
            {isQrModalOpen && (
              <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  transition={{ duration: 0.2 }}
                  className="relative w-full max-w-[320px] overflow-hidden rounded-[24px] border border-[#6D13A6] bg-gradient-to-r from-[#02062E]/80 from-[51%] to-[#350C43]/80 shadow-2xl pb-4 pt-1 px-3"
                >
                  <div className="flex items-center justify-center p-3 relative">
                    <h3 className="text-[15px] font-semibold text-white">
                      Referral Qr Code
                    </h3>
                    <button
                      type="button"
                      onClick={() => setIsQrModalOpen(false)}
                      className="absolute right-3 flex size-7 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                    >
                      <X size={14} />
                    </button>
                  </div>

                  <div className="bg-white/10 px-2 pb-4 pt-2 rounded-[16px]">
                    <div className="w-full aspect-square rounded-[16px] bg-white p-3 flex items-center justify-center relative overflow-hidden">
                      <Image
                        src="/Final_NovaMobile_Assets/05_Refer&EarnNew/qrcode.png"
                        alt="QR Code"
                        fill
                        className="object-contain p-2"
                      />
                    </div>
                  </div>

                  <div className="px-4 mt-2 mb-2">
                    <button
                      type="button"
                      onClick={handleSaveQr}
                      className="w-full flex flex-col items-center justify-center rounded-[16px] border border-white/10 bg-gradient-to-b from-[#7e22ce] to-[#4c1d95] py-3 transition-colors hover:from-[#7e22ce]/80 hover:to-[#4c1d95]/80 active:scale-95"
                    >
                      <span className="text-[11px] font-medium text-white/90">
                        Save QR Image
                      </span>
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>

          {/* Toast */}
          <AnimatePresence>
            {toastMessage && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="fixed top-8 left-1/2 -translate-x-1/2 z-[200] flex items-center gap-2 rounded-full border border-white/10 bg-gradient-to-r from-black/80 via-[#2E0B4E]/80 to-black/80 px-5 py-2.5 shadow-[0_0_15px_rgba(255,255,255,0.1)] backdrop-blur-md"
              >
                <CheckCircle size={16} className="text-[#FFA035]" />
                <span className="text-[11px] xs:text-[13px] text-white/90 font-medium tracking-wide">
                  {toastMessage}
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </AnimatePresence>
  );
};
